import { useState } from 'react'
import { DateRangePicker } from './DateRangePicker'
import { GuestRoomSelector } from './GuestRoomSelector'
import { DestinationInput } from './DestinationInput'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { todayManila, addDays } from '@/lib/format'
import { addRecentSearch } from '@/lib/recentSearches'
import type { ResolvedSearchParams } from '@/lib/schemas/search'

interface SearchBarProps {
  /** Seed values (from URL params on the results page, or defaults on home) */
  initialValues?: Partial<ResolvedSearchParams>
  onSearch: (values: Partial<ResolvedSearchParams>) => void
  /** 'hero' = large white card on green background; 'inline' = compact bar */
  variant?: 'hero' | 'inline'
  className?: string
}

export function SearchBar({
  initialValues,
  onSearch,
  variant = 'hero',
  className,
}: SearchBarProps) {
  const today    = todayManila()
  const tomorrow = addDays(today, 1)

  const [city,     setCity]     = useState(initialValues?.city      ?? '')
  const [province, setProvince] = useState(initialValues?.province  ?? '')
  const [checkIn,  setCheckIn]  = useState(initialValues?.check_in  ?? today)
  const [checkOut, setCheckOut] = useState(initialValues?.check_out ?? tomorrow)
  const [adults,    setAdults]    = useState(initialValues?.adults    ?? 1)
  const [children,  setChildren]  = useState(initialValues?.children  ?? 0)
  const [childAges, setChildAges] = useState<number[]>(initialValues?.child_ages ?? [])
  const [rooms,     setRooms]     = useState(initialValues?.rooms     ?? 1)

  function handleCheckInChange(val: string) {
    const newOut = val >= checkOut ? addDays(val, 1) : checkOut
    setCheckIn(val)
    setCheckOut(newOut)
  }

  function handleGuestChange(v: { adults: number; children: number; rooms: number; child_ages: number[] }) {
    setAdults(v.adults)
    setChildren(v.children)
    setChildAges(v.child_ages)
    setRooms(v.rooms)
  }

  /** Run a search from the current field values, optionally overriding some
   *  (used so selecting a suggestion can search immediately without waiting on
   *  a state update). */
  function runSearch(over: Partial<ResolvedSearchParams> = {}) {
    const payload: Partial<ResolvedSearchParams> = {
      city, province, check_in: checkIn, check_out: checkOut,
      adults, children, child_ages: childAges, rooms,
      ...over,
    }
    if (payload.city?.trim()) addRecentSearch(payload.city.trim())
    onSearch(payload)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    runSearch()
  }

  // Typing manually clears any remembered province (it no longer applies).
  function handleDestChange(v: string) {
    setCity(v)
    setProvince('')
  }

  // Picking a suggestion fills the field, remembers its province, and searches.
  function handleDestSelect(sel: { value: string; province?: string }) {
    const prov = sel.province ?? ''
    setCity(sel.value)
    setProvince(prov)
    runSearch({ city: sel.value, province: prov })
  }

  // ── Inline variant (compact bar used on SearchResults) ────────────────────
  if (variant === 'inline') {
    return (
      <form
        onSubmit={handleSubmit}
        className={cn(
          'flex flex-wrap items-end gap-2 rounded-xl border border-gray-200 bg-white p-3 shadow-sm',
          className,
        )}
      >
        {/* Destination */}
        <div className="flex min-w-[140px] flex-1 flex-col gap-0.5">
          <label className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Destination
          </label>
          <DestinationInput
            value={city}
            onChange={handleDestChange}
            onSelect={handleDestSelect}
            placeholder="City or region"
            inputClassName="h-10 w-full rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kasa-green-500 focus-visible:ring-offset-1"
          />
        </div>

        {/* Date range */}
        <div className="min-w-[240px] flex-[2]">
          <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Dates
          </p>
          <DateRangePicker
            checkIn={checkIn}
            checkOut={checkOut}
            onChange={(ci, co) => { setCheckIn(ci); setCheckOut(co) }}
            inline
          />
        </div>

        {/* Guests + rooms */}
        <div className="min-w-[160px] flex-1">
          <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500">
            Guests
          </p>
          <GuestRoomSelector
            adults={adults}
            children={children}
            childAges={childAges}
            rooms={rooms}
            onChange={handleGuestChange}
          />
        </div>

        <Button type="submit" variant="cta" className="h-10 shrink-0 px-5">
          Search
        </Button>
      </form>
    )
  }

  // ── Hero variant — Booking.com-style horizontal strip ─────────────────────
  //
  // Desktop: [Destination | Check-in | Check-out | Guests & Rooms] + [Search]
  //          all in one row, labels on top, inputs below, separated by dividers
  // Mobile:  stacked fields, full-width search button at bottom
  //
  const labelClass = 'block text-[11px] font-bold uppercase tracking-widest text-gray-500 mb-1 select-none'
  const inputClass = cn(
    'h-12 w-full bg-transparent border-0 p-0 text-base font-medium text-gray-900',
    'placeholder:font-normal placeholder:text-gray-400',
    'focus:outline-none focus:ring-0',
  )
  const dateInputClass = cn(inputClass, 'cursor-pointer [color-scheme:light]')

  return (
    <form
      onSubmit={handleSubmit}
      className={cn('w-full max-w-4xl', className)}
    >
      {/* ── Desktop single-row card ─────────────────────────────────────── */}
      <div className="hidden rounded-2xl bg-white p-1 shadow-2xl ring-1 ring-white/20 sm:flex sm:items-stretch">

        {/* Destination */}
        <div className="flex flex-1 flex-col px-5 py-3.5 min-w-0">
          <label className={labelClass}>Where are you going?</label>
          <DestinationInput
            value={city}
            onChange={handleDestChange}
            onSelect={handleDestSelect}
            placeholder="Destination, city, or region"
            inputClassName={inputClass}
          />
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-200 my-3 shrink-0" />

        {/* Check-in */}
        <div className="flex flex-col px-5 py-3.5 min-w-[140px]">
          <label className={labelClass}>Check-in</label>
          <input
            type="date"
            value={checkIn}
            min={today}
            onChange={(e) => handleCheckInChange(e.target.value)}
            className={dateInputClass}
          />
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-200 my-3 shrink-0" />

        {/* Check-out */}
        <div className="flex flex-col px-5 py-3.5 min-w-[140px]">
          <label className={labelClass}>Check-out</label>
          <input
            type="date"
            value={checkOut}
            min={addDays(checkIn, 1)}
            onChange={(e) => setCheckOut(e.target.value)}
            className={dateInputClass}
          />
        </div>

        {/* Divider */}
        <div className="w-px bg-gray-200 my-3 shrink-0" />

        {/* Guests & rooms */}
        <div className="flex flex-col px-5 py-3.5 min-w-[180px]">
          <label className={labelClass}>Guests &amp; rooms</label>
          <GuestRoomSelector
            adults={adults}
            children={children}
            childAges={childAges}
            rooms={rooms}
            onChange={handleGuestChange}
            className="h-12 border-0 px-0 text-base font-medium shadow-none hover:border-0"
          />
        </div>

        {/* Search button — flush right, fills card height */}
        <div className="shrink-0 p-1.5 pl-2">
          <button
            type="submit"
            className="flex h-full min-w-[100px] items-center justify-center rounded-xl bg-kasa-amber px-6 text-sm font-bold text-white shadow-md transition-colors hover:bg-kasa-amber-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kasa-amber focus-visible:ring-offset-2"
          >
            Search
          </button>
        </div>
      </div>

      {/* ── Mobile stacked card ─────────────────────────────────────────── */}
      <div className="flex flex-col gap-2 rounded-2xl bg-white p-4 shadow-2xl ring-1 ring-white/20 sm:hidden">

        {/* Destination */}
        <div className="flex flex-col">
          <label className={cn(labelClass, 'text-gray-600')}>Where are you going?</label>
          <DestinationInput
            value={city}
            onChange={handleDestChange}
            onSelect={handleDestSelect}
            placeholder="Destination, city, or region"
            inputClassName="h-12 w-full rounded-xl border border-gray-200 px-4 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-kasa-green-500"
          />
        </div>

        {/* Dates side by side */}
        <div className="grid grid-cols-2 gap-2">
          <div className="flex flex-col">
            <label className={cn(labelClass, 'text-gray-600')}>Check-in</label>
            <input
              type="date"
              value={checkIn}
              min={today}
              onChange={(e) => handleCheckInChange(e.target.value)}
              className="h-12 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-kasa-green-500 [color-scheme:light]"
            />
          </div>
          <div className="flex flex-col">
            <label className={cn(labelClass, 'text-gray-600')}>Check-out</label>
            <input
              type="date"
              value={checkOut}
              min={addDays(checkIn, 1)}
              onChange={(e) => setCheckOut(e.target.value)}
              className="h-12 rounded-xl border border-gray-200 px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-kasa-green-500 [color-scheme:light]"
            />
          </div>
        </div>

        {/* Guests & rooms */}
        <div className="flex flex-col">
          <label className={cn(labelClass, 'text-gray-600')}>Guests &amp; rooms</label>
          <GuestRoomSelector
            adults={adults}
            children={children}
            childAges={childAges}
            rooms={rooms}
            onChange={handleGuestChange}
            className="h-12 rounded-xl border border-gray-200"
          />
        </div>

        {/* Search */}
        <button
          type="submit"
          className="mt-1 h-12 w-full rounded-xl bg-kasa-amber text-base font-bold text-white shadow-md hover:bg-kasa-amber-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-kasa-amber"
        >
          Search
        </button>
      </div>
    </form>
  )
}
